import { useEffect, useState } from "react";
import { Globe, Check, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api from "@/lib/api";
import { setLanguage } from "@/i18n";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

let statusCache = null; // fetched once per session

export default function LanguageSelector({ dark = false, persistToProfile = false }) {
  const { i18n, t } = useTranslation();
  const [langs, setLangs] = useState(statusCache);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    if (statusCache) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/i18n/status`)
      .then((r) => r.json())
      .then((d) => { statusCache = d.languages; setLangs(d.languages); })
      .catch(() => setLangs([{ code: "en", native: "English", status: "ready" }]));
  }, []);

  const pick = async (code, status) => {
    if (status !== "ready" && status !== "stale" && code !== "en") return;
    setBusy(code);
    try {
      await setLanguage(code);
      if (persistToProfile && localStorage.getItem("aetria_token")) {
        api.patch("/auth/me/language", { lang: code }).catch(() => {});
      }
    } catch {
      toast.error(t("common.languageUnavailable"));
    } finally {
      setBusy(null);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="language-selector"
          aria-label={t("common.language")}
          className={`inline-flex items-center gap-1.5 px-2 py-1.5 text-[12px] transition-colors ${
            dark ? "text-slate-300 hover:text-white" : "text-slate-500 hover:text-slate-800"}`}>
          <Globe className="h-4 w-4" />
          <span className="uppercase font-mono text-[11px]">{i18n.language}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="rounded-none w-48">
        {(langs || []).map(({ code, native, status = "ready" }) => {
          const ready = code === "en" || status === "ready" || status === "stale";
          return (
            <DropdownMenuItem key={code} disabled={!ready}
              data-testid={`lang-option-${code}`}
              onSelect={(e) => { e.preventDefault(); pick(code, status); }}
              className="rounded-none text-[13px] cursor-pointer flex items-center justify-between">
              <span>{native}</span>
              {busy === code ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : i18n.language === code ? <Check className="h-3.5 w-3.5 text-teal-600" />
                : !ready ? <span className="text-[10px] text-slate-400">{t("common.generating")}</span>
                : null}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
